import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { LayoutGrid, Sparkles, ExternalLink } from 'lucide-react';

/**
 * "App Switcher" — a grid menu linking to sibling apps in the bundle.
 * `apps` come from GET /api/sso/apps (registered SSO apps that have a launch URL).
 * PostToSocial is always shown first as the current app.
 *
 * `trigger`: "icon" (compact grid button) | "nav" (full-width sidebar row).
 */
export const AppSwitcher = ({ apps = [], trigger = 'icon', onNavigate }) => {
  const [open, setOpen] = useState(false);

  const tiles = [
    { name: 'PostToSocial', home_url: null, current: true, logo_url: '' },
    ...apps.map((a) => ({ ...a, current: false })),
  ];

  const TriggerEl =
    trigger === 'nav' ? (
      <button
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
        data-testid="app-switcher-nav-trigger"
      >
        <LayoutGrid className="w-5 h-5" />
        <span className="flex-1 text-left">Our Apps</span>
      </button>
    ) : (
      <Button variant="ghost" size="icon" className="h-9 w-9 text-zinc-500 hover:text-indigo-600" data-testid="app-switcher-icon-trigger" aria-label="Switch apps">
        <LayoutGrid className="w-5 h-5" />
      </Button>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{TriggerEl}</PopoverTrigger>
      <PopoverContent align={trigger === 'nav' ? 'start' : 'end'} className="w-72 p-3" data-testid="app-switcher-panel">
        <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide px-1 pb-2">Your apps</p>
        <div className="grid grid-cols-2 gap-2">
          {tiles.map((app) => {
            const inner = (
              <div className="flex flex-col items-center justify-center gap-2 p-3 rounded-xl border border-zinc-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors text-center h-24">
                {app.logo_url ? (
                  <img src={app.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                )}
                <span className="text-xs font-medium text-zinc-700 truncate max-w-full flex items-center gap-1">
                  {app.name}
                  {!app.current && <ExternalLink className="w-3 h-3 text-zinc-400 shrink-0" />}
                </span>
                {app.current && <span className="text-[10px] text-emerald-600 font-medium -mt-1">Current</span>}
              </div>
            );
            if (app.current) {
              return <div key="current" data-testid="app-switcher-current">{inner}</div>;
            }
            return (
              <a
                key={app.home_url}
                href={app.home_url}
                onClick={() => { setOpen(false); onNavigate?.(); }}
                data-testid={`app-switcher-app-${app.name}`}
                className="block"
              >
                {inner}
              </a>
            );
          })}
        </div>
        {tiles.length === 1 && (
          <p className="text-xs text-zinc-400 text-center pt-3">No other apps yet.</p>
        )}
      </PopoverContent>
    </Popover>
  );
};
